import { useEffect, useRef, useState } from 'react';

import { visiblePercentage } from '@/lib/dom';
import { objectKeys } from '@/lib/types';

export type SectionSelectorMap<T> = {
  [key in keyof T]: string;
};

export type SectionElementMap<T> = {
  [key in keyof T]: Element;
};

export type SectionVisibilityMap<T> = {
  [key in keyof T]: number;
};

export type MostVisibleSection<M> = {
  section: keyof M;
  visibility: number;
};

export function useScrollTracking<M extends SectionSelectorMap<T>, T>(
  currentSection: keyof M,
  sectionMap: M,
  container: string,
  onSectionChange: (section: keyof M) => void,
  dependencies: any[] = [],
) {
  const autoSelectedSectionRef = useRef<keyof M>();
  const currentSectionRef = useRef<keyof M>();
  currentSectionRef.current = currentSection;

  const [containerElement, setContainerElement] = useState<Element>();
  const [sectionElements, setSectionElements] = useState<SectionElementMap<M>>(
    {} as SectionElementMap<M>,
  );

  useEffect(() => {
    const element = document.querySelector(container);
    if (element) {
      setContainerElement(element);
      const elements = {} as SectionElementMap<M>;
      objectKeys(sectionMap).forEach((section) => {
        const element = document.querySelector(sectionMap[section]);
        if (element) {
          elements[section] = element;
        }
      });
      setSectionElements(elements);
    }
  }, [container, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // the user manually scrolled to a section, so skip scrolling into view
    if (autoSelectedSectionRef.current === currentSection) return;

    const target = document.querySelector(sectionMap[currentSection]);
    if (target) {
      target.scrollIntoView({
        behavior: 'instant',
      });
    }
  }, [currentSection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (containerElement) {
      const handleScroll = () => {
        const isAtBottom =
          containerElement.scrollHeight - containerElement.scrollTop <=
          containerElement.clientHeight + 10;

        if (isAtBottom) {
          const lastSection = objectKeys(sectionMap).pop()!;
          if (currentSectionRef.current !== lastSection) {
            autoSelectedSectionRef.current = lastSection;
            onSectionChange(lastSection);
          }
        }
      };

      const observer = new IntersectionObserver(
        (entries) => {
          const visibilities = {} as SectionVisibilityMap<M>;
          objectKeys(sectionMap).forEach((section) => {
            visibilities[section] = visiblePercentage(
              containerElement,
              sectionElements[section],
            );
          });

          let mostVisibleSection: MostVisibleSection<M> = {
            section: currentSection,
            visibility: visibilities[currentSection],
          };
          objectKeys(visibilities).forEach((section) => {
            if (visibilities[section] > mostVisibleSection.visibility) {
              mostVisibleSection = {
                section,
                visibility: visibilities[section],
              };
            }
          });

          if (mostVisibleSection.section !== currentSectionRef.current) {
            autoSelectedSectionRef.current = mostVisibleSection.section;
            onSectionChange(mostVisibleSection.section);
          }
        },
        {
          root: containerElement,
          rootMargin: '0px 0px -100px 0px',
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9],
        },
      );

      objectKeys(sectionMap).forEach((section) => {
        observer.observe(sectionElements[section]);
      });

      containerElement.addEventListener('scroll', handleScroll);

      return () => {
        observer.disconnect();
        containerElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [
    containerElement,
    sectionMap,
    onSectionChange,
    currentSection,
    sectionElements,
  ]);

  const handleManualSelection = () => {
    autoSelectedSectionRef.current = undefined;
  };

  return {
    onManualSelection: handleManualSelection,
  };
}
